<template>
  <div class="ski-log-container">
    <LoadingSpinner v-if="loading" />
    <template v-else>
      <div class="header">
        <h1>Ski Logs</h1>
        <button @click="createNewLog" class="action-button primary">
          + New Ski Session
        </button>
      </div>

      <!-- Stats Summary -->
      <div v-if="stats" class="stats-card">
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-number">{{ stats.total_logs }}</span>
            <span class="stat-label">Sessions</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ stats.total_passes }}</span>
            <span class="stat-label">Total Passes</span>
          </div>
          <div class="stat-item" v-if="stats.most_used_boat">
            <span class="stat-number">{{ getBoatDisplay(stats.most_used_boat.boat) }}</span>
            <span class="stat-label">Favorite Boat</span>
          </div>
          <div class="stat-item" v-if="shortestLineData">
            <span class="stat-number">{{ shortestLineData.buoys }}</span>
            <span class="stat-label">{{ getRopeDisplay(shortestLineData.ropeLength) }}</span>
          </div>
        </div>
      </div>

      <!-- Ski Logs List -->
      <div class="logs-list">
        <div 
          v-for="log in logs" 
          :key="log.id" 
          class="log-card"
          @click="openLogDetail(log.id)"
        >
          <div class="log-header">
            <div class="log-title">
              <h3>{{ formatLogTitle(log) }}</h3>
              <span class="log-date">{{ formatDate(log.date || log.created) }}</span>
            </div>
            <div class="log-summary">
              <span v-if="log.get_total_passes" class="info-badge">
                {{ log.get_total_passes }} passes
              </span>
              <span v-if="log.conditions" class="info-badge">
                {{ log.conditions }}% conditions
              </span>
            </div>
          </div>
          
          <div class="log-details">
            <div class="log-info-row" v-if="log.lake">
              <span class="label">Lake:</span>
              <span class="value">{{ log.lake }}</span>
            </div>
            <div class="log-info-row" v-if="log.boat">
              <span class="label">Boat:</span>
              <span class="value">{{ getBoatDisplay(log.boat) }}</span>
              <span v-if="log.boat_type" class="value-detail">{{ log.boat_type }}</span>
            </div>
            <div class="log-info-row" v-if="log.ski">
              <span class="label">Ski:</span>
              <span class="value">{{ getSkiDisplay(log.ski) }}</span>
              <span v-if="log.ski_type" class="value-detail">{{ log.ski_type }}</span>
            </div>
            <div class="log-info-row" v-if="log.driver">
              <span class="label">Driver:</span>
              <span class="value">{{ log.driver }}</span>
            </div>
            <div class="log-info-row" v-if="log.conditions_detail">
              <span class="label">Conditions:</span>
              <span class="value">{{ log.conditions_detail }}</span>
            </div>
          </div>

          <!-- Quick feedback preview -->
          <div v-if="log.positive || log.negative" class="feedback-preview">
            <p v-if="log.positive" class="feedback-positive">
              ✓ {{ truncateText(log.positive, 100) }}
            </p>
            <p v-if="log.negative" class="feedback-negative">
              ✗ {{ truncateText(log.negative, 100) }}
            </p>
          </div>
        </div>

        <!-- Load More Button -->
        <button 
          v-if="hasMoreLogs && !loading" 
          @click="loadMoreLogs" 
          class="action-button secondary load-more-btn"
        >
          Load More
        </button>

        <!-- Empty State -->
        <div v-if="logs.length === 0 && !loading" class="empty-state">
          <h3>No ski logs yet</h3>
          <p>Start tracking your ski sessions to improve your performance!</p>
          <button @click="createNewLog" class="action-button primary">
            Create Your First Log
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export default {
  name: 'SkiLog',
  components: {
    LoadingSpinner
  },
  data() {
    return {
      logs: [],
      stats: null,
      loading: true,
      limit: 10,
      offset: 0,
      hasMoreLogs: true,
      boatChoices: {
        'nautique': 'Nautique',
        'mastercraft': 'MasterCraft',
        'malibu': 'Malibu',
        'centurion': 'Centurion',
        'other': 'Other',
      },
      skiChoices: {
        'connelly': 'Connelly',
        'd3': 'D3',
        'goode': 'Goode',
        'ho': 'HO',
        'kd': 'KD',
        'radar': 'Radar',
        'reflex': 'Reflex',
        'obrien': 'Obrien',
        'denali': 'Denali',
        'pineapple': 'Pineapple',
        'lapoint': 'Lapoint',
        'other': 'Other',
      },
      ropeChoices: {
        '18.25': '18.25m (15 Off)',
        '16.00': '16.00m (22 Off)',
        '14.25': '14.25m (28 Off)',
        '13.00': '13.00m (32 Off)',
        '12.00': '12.00m (35 Off)',
        '11.25': '11.25m (38 Off)',
        '10.75': '10.75m (39.5 Off)',
        '10.25': '10.25m (41 Off)',
        '9.75': '9.75m (43 Off)',
      }
    };
  },
  computed: {
    shortestLineData() {
      return this.calculateShortestLineData();
    }
  },
  async mounted() {
    await this.fetchLogs();
    await this.fetchStats();
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/skilog/logs/?limit=${this.limit}&offset=${this.offset}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (this.offset === 0) {
          this.logs = response.data.logs;
        } else {
          this.logs = [...this.logs, ...response.data.logs];
        }

        this.hasMoreLogs = response.data.logs.length === this.limit;
        this.offset += this.limit;
      } catch (error) {
        console.error('Error fetching ski logs:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/skilog/stats/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.stats = response.data;
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async loadMoreLogs() {
      await this.fetchLogs();
    },

    createNewLog() {
      this.$router.push({ name: 'SkiLogCreate' });
    },

    openLogDetail(logId) {
      this.$router.push({ name: 'SkiLogDetail', params: { id: logId } });
    },

    formatLogTitle(log) {
      if (log.lake) {
        return `Session at ${log.lake}`;
      }
      return `Ski Session #${log.id}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'No date';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },

    getBoatDisplay(boatKey) {
      return this.boatChoices[boatKey] || boatKey;
    },

    getSkiDisplay(skiKey) {
      return this.skiChoices[skiKey] || skiKey;
    },

    getRopeDisplay(ropeLength) {
      return this.ropeChoices[ropeLength.toString()] || `${ropeLength}m`;
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    calculateShortestLineData() {
      if (!this.logs || this.logs.length === 0) {
        return null;
      }

      let shortestRopeLength = null;
      let shortestLineBuoys = null;

      // Iterate through all logs and their passes to find the shortest rope length
      for (const log of this.logs) {
        if (log.passes && log.passes.length > 0) {
          for (const pass of log.passes) {
            if (pass.rope_length && pass.buoys !== null && pass.buoys !== undefined) {
              const ropeLength = parseFloat(pass.rope_length);
              if (shortestRopeLength === null || ropeLength < shortestRopeLength) {
                shortestRopeLength = ropeLength;
                shortestLineBuoys = pass.buoys;
              }
            }
          }
        }
      }

      return shortestRopeLength ? { ropeLength: shortestRopeLength, buoys: shortestLineBuoys } : null;
    }
  }
};
</script>

<style scoped>
.ski-log-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.header h1 {
  color: #ffffff;
  margin: 0;
  font-size: 2rem;
}

.stats-card {
  background-color: #192734;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 30px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 20px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  color: #1da1f2;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 4px;
}

.stat-label {
  color: #8899a6;
  font-size: 0.9rem;
}

.logs-list {
  margin-top: 20px;
}

.log-card {
  background-color: #192734;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.log-card:hover {
  background-color: #22303c;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.log-title h3 {
  color: #ffffff;
  margin: 0 0 4px 0;
  font-size: 1.2rem;
}

.log-date {
  color: #8899a6;
  font-size: 0.9rem;
}

.log-summary {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.info-badge {
  background-color: #1da1f2;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.log-details {
  margin-bottom: 15px;
}

.log-info-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.label {
  color: #8899a6;
  font-size: 0.9rem;
  min-width: 60px;
}

.value {
  color: #ffffff;
  font-weight: 500;
}

.value-detail {
  color: #8899a6;
  font-style: italic;
}

.feedback-preview {
  border-top: 1px solid #38444d;
  padding-top: 15px;
  margin-top: 15px;
}

.feedback-positive {
  color: #2ecc71;
  margin: 0 0 8px 0;
  font-size: 0.9rem;
}

.feedback-negative {
  color: #e74c3c;
  margin: 0 0 8px 0;
  font-size: 0.9rem;
}

.action-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.primary {
  background-color: #1da1f2;
  color: white;
}

.action-button.primary:hover {
  background-color: #1991db;
}

.action-button.secondary {
  background-color: #8899a6;
  color: white;
}

.action-button.secondary:hover {
  background-color: #7a8a97;
}

.load-more-btn {
  width: 100%;
  margin-top: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #8899a6;
}

.empty-state h3 {
  color: #ffffff;
  margin-bottom: 16px;
}

.empty-state p {
  margin-bottom: 24px;
  font-size: 1.1rem;
}

@media screen and (max-width: 600px) {
  .ski-log-container {
    padding: 16px;
  }
  
  .header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .log-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .log-summary {
    justify-content: flex-start;
  }
}
</style>
